import { InputNumber, RadioButtonGroup, Select, Tooltip } from '@repo/go-ui';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import type { HeatmapWidgetState } from '../../store/HeatmapWidgetState';
import { IntervalsTypes, OperationRegimes } from '../../store/types';
import { INTERVAL_OPTIONS } from '../SettingsDrawer/SettingsOptions';

import styles from './control-panel.module.scss';

type IntervalControlConfig = {
  value: number | null;
  setValue: (value: number) => void;
};

export const ControlPanel = observer(({ widgetState, width }: { widgetState: HeatmapWidgetState; width: number }) => {
  const { t } = useTranslation('heatmapWidget');
  const settings = widgetState.settings;
  const intervalControls: Partial<Record<IntervalsTypes, IntervalControlConfig>> = {
    [IntervalsTypes.FORMATIONS]: {
      value: widgetState.currentFormationId,
      setValue: (value) => widgetState.setCurrentFormationId(value),
    },
    [IntervalsTypes.STRAT_UNITS]: {
      value: widgetState.currentStratUnitId,
      setValue: (value) => widgetState.setCurrentStratUnitId(value),
    },
    [IntervalsTypes.SECTIONS]: {
      value: widgetState.currentSectionId,
      setValue: (value) => widgetState.setCurrentSectionId(value),
    },
    [IntervalsTypes.DRILLING_ANALYSIS]: {
      value: widgetState.currentDrillingAnalysisGroupId,
      setValue: (value) => widgetState.setCurrentDrillingAnalysisGroupId(value),
    },
  };
  const intervalControl = intervalControls[settings.interval];
  const shouldRenderIntervalSelect = settings.interval !== IntervalsTypes.USER_DEFINED && intervalControl;

  const intervalOption = INTERVAL_OPTIONS.find((option) => option.value === settings.interval);

  return (
    <div className={clsx(styles.wrapper, { [styles.compact]: width <= 800 })}>
      <div className={clsx(styles.leftSide, { [styles.compact]: width <= 800 })}>
        <span className={styles.label}>{`${t('Depth')}:`}</span>
        <div className={styles.depth}>
          <label>
            <span>{`${t('Depth')} ${t('Min')}`}</span>
            <InputNumber
              size="s"
              value={settings.startDepth}
              min={0}
              disabled={settings.interval !== IntervalsTypes.USER_DEFINED}
              onChange={(value) => settings.setDepthRange({ startDepth: Number(value) })}
              suffix={settings.depthUnit}
            />
          </label>
          <span>-</span>
          <Tooltip
            title={t('Heatmap-depth-unlimited-hint')}
            open={settings.endDepth === null && !widgetState.isSuppressTooltip ? undefined : false}
          >
            <label>
              <span>{`${t('Depth')} ${t('Max')}`}</span>
              <InputNumber
                size="s"
                value={settings.endDepth}
                min={0}
                placeholder={t('Max')}
                allowClear
                disabled={settings.interval !== IntervalsTypes.USER_DEFINED}
                onChange={(value) =>
                  settings.setDepthRange({
                    endDepth: value === null || value === '' ? null : Number(value),
                  })
                }
                suffix={settings.endDepth === null ? undefined : settings.depthUnit}
              />
            </label>
          </Tooltip>
        </div>

        {shouldRenderIntervalSelect && intervalControl && (
          <>
            <span className={styles.label}>{`${t(intervalOption?.shortLabel ?? intervalOption?.label ?? '')}:`}</span>
            <Select
              options={widgetState.getOptionsForInterval(settings.interval, t('Max'))}
              value={intervalControl.value ?? undefined}
              onSelect={(value) => intervalControl.setValue(value)}
              style={{ minWidth: 100 }}
              dropdownMatchSelectWidth={false}
              dropdownStyle={{ minWidth: 235 }}
            />
          </>
        )}
      </div>
      <div className={styles.rightSide}>
        <RadioButtonGroup
          style={{ width: 103 }}
          items={[
            {
              label: t('Rotation-label'),
              value: OperationRegimes.ROTATION,
            },
            {
              label: t('Slide-label'),
              value: OperationRegimes.SLIDE,
            },
          ]}
          value={settings.operationRegimes}
          onChange={(value) => settings.setOperationRegimes(value)}
        />
      </div>
    </div>
  );
});
